import React from 'react'
import styled from 'styled-components'
import { ButtonPrimary, ButtonSecondary } from '../../../components/Button'
import { SkeletonText } from '../../../components/Skeleton'
import { useActiveWeb3React } from '../../../hooks'
import { calculateGasMargin, getContract } from '../../../utils'
import { BigNumber } from 'ethers'
import { formatUnits } from 'ethers/lib/utils'
import { roundToDecimalPlace } from '../../../utils/data'
import { useToken } from '../../../hooks/Tokens'
import { useTransactionAdder } from '../../../state/transactions/hooks'
import { timestampToCountdown } from './RedeemStakeCard'
import { abi as VestingABI } from 'abis/vesting.json'

const GridContainer = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: 1fr;

  /*   @media (min-width: 768px) {
    grid-template-columns: calc(60% - 8px) calc(40% - 8px);
  } */
`

const EntriesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(0px, 1fr));
  gap: 15px 0.5rem;
  padding: 0;
`

const SubContainer = styled.div`
  padding: 20px;
  background: rgb(23, 23, 23);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`

interface VestingProps {
  contractAddress: string
}

function formatUnixTimestamp(unixTimestamp: string) {
  const date = new Date(parseInt(unixTimestamp) * 1000) // Convert to milliseconds
  const dateOptions = { month: '2-digit', day: '2-digit', year: 'numeric' }
  const timeOptions = { hour: '2-digit', minute: '2-digit', hour12: false }

  const formattedDate = date.toLocaleDateString('en-US', dateOptions as any)
  const formattedTime = date.toLocaleTimeString('en-US', timeOptions as any)

  return `${formattedDate} ${formattedTime}`
}

function getEndDate(startDateUnix: string, durationInSeconds: number) {
  // Convert start date from Unix timestamp to milliseconds and create a Date object
  const startDate = new Date(parseInt(startDateUnix) * 1000)

  // Calculate the end date by adding the duration in milliseconds
  const endDate = new Date(startDate.getTime() + durationInSeconds * 1000)

  // Format the end date as MM-DD-YYYY
  const options = { month: '2-digit', day: '2-digit', year: 'numeric' }
  return endDate.toLocaleDateString('en-US', options as any)
}
const cropAddress = (addr: string): string => {
  if (!addr) return ''
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`
}
const Vesting: React.FC<VestingProps> = ({ contractAddress }) => {
  const { account, chainId, library } = useActiveWeb3React()

  const [data, setData] = React.useState<any | undefined>()
  const [loading, setLoading] = React.useState<boolean>(true)
  const token = useToken(data ? data.token : '')
  const tokenSymbol = token?.symbol ? token.symbol : ''
  const [loadingClaim, setLoadingClaim] = React.useState<boolean>(false)
  const addTransaction = useTransactionAdder()
  const [buttonLabel, setButtonLabel] = React.useState('Copy')

  const cliffDate = data?.cliffDate ? data?.cliffDate.toString() : '0'
  const cliffDateFormatted = formatUnixTimestamp(cliffDate)
  const deadline = timestampToCountdown(cliffDate)
  const tokenAddress = token?.address ? cropAddress(token?.address) : ''
  const isPast = deadline?.isPast

  const copyToClipboard = (address: string) => {
    navigator.clipboard.writeText(address).then(() => {
      setButtonLabel('Copied!') // Temporarily change button label to "Copied"
      setTimeout(() => setButtonLabel('Copy'), 3000) // Reset to "Copy" after 3 seconds
    })
  }

  React.useEffect(() => {
    async function fetchData() {
      if (!chainId || !library || !account) return

      const vestingContract = getContract(contractAddress, VestingABI, library, account)
      const duration = await vestingContract.duration()
      const startTime = await vestingContract.startTime()

      const available = await vestingContract.available(account)
      const allocation = await vestingContract.allocation(account)
      const released = await vestingContract.released(account)
      const outstanding = await vestingContract.outstanding(account)
      const claimed = await vestingContract.claimed(account)
      const token = await vestingContract.token()
      const name = await vestingContract.name()
      const cliffDate = await vestingContract.cliffDate()

      setData({
        duration,
        startTime,
        available,
        allocation,
        outstanding,
        released,
        claimed,
        token,
        name,
        cliffDate,
      })

      setLoading(false)
    }

    if (loading) {
      fetchData()
    }
  }, [loading, account, chainId, library, contractAddress])

  const startDate = data?.startTime ? data?.startTime.toString() : '0'
  const startDateFormatted = formatUnixTimestamp(startDate)

  const duration = data?.duration ? data?.duration : BigNumber.from(0)
  const durationFormatted = duration.toNumber()
  const endDate = getEndDate(startDate, durationFormatted)

  const available = data?.available ? data?.available : BigNumber.from(0)
  const availableFormatted = formatUnits(available, 18)

  const allocation = data?.allocation ? data.allocation : BigNumber.from(0)
  const allocationFormatted = formatUnits(allocation, 18)

  const outstanding = data?.outstanding ? data?.outstanding : BigNumber.from(0)
  const outstandingFormatted = formatUnits(outstanding, 18)

  const claimed = data?.claimed ? data?.claimed : BigNumber.from(0)
  const claimedFormatted = formatUnits(claimed, 18)

  async function onClaim() {
    if (!chainId || !library || !account) return
    const vestingContract = getContract(contractAddress, VestingABI, library, account)

    const estimate = vestingContract.estimateGas.claim
    const method = vestingContract.claim

    const args = [] as any

    setLoadingClaim(true)

    await estimate(...args, {})
      .then((estimatedGasLimit: any) => {
        return method(...args, {
          gasLimit: calculateGasMargin(estimatedGasLimit),
        }).then(async (response: any) => {
          addTransaction(response, {
            summary: `Claim`,
          })

          // Wait for the transaction to be mined
          await response.wait()
          setLoading(true)
          setLoadingClaim(false)
        })
      })
      .catch((error) => {
        setLoadingClaim(false)
        console.error(error)
      })
  }

  return (
    <GridContainer>
      <SubContainer>
        <>
          <div style={{ display: 'flex', fontSize: '14px', justifyContent: 'space-between', alignItems: 'center' }}>
            {data ? <h2 style={{ fontSize: '14px' }}>{data.name}</h2> : <SkeletonText width="50%" />}
          </div>
          <div
            style={{
              width: '100%',
              borderBottom: '0.0625rem solid black',
              height: '0px',
              marginTop: '14px',
              marginBottom: '20px',
            }}
          ></div>

          <EntriesContainer>
            <span style={{ fontWeight: 600 }}>Start Date</span>
            <span style={{ fontWeight: 600 }}>End Date</span>
            <span style={{ fontWeight: 600 }}>Allocation</span>
          </EntriesContainer>
          <EntriesContainer>
            <span>{data ? startDateFormatted : <SkeletonText width="30%" />}</span>
            <span>{data ? endDate : <SkeletonText width="30%" />}</span>
            <span>
              {!loading ? (
                `${roundToDecimalPlace(allocationFormatted, '3')} $${tokenSymbol}`
              ) : (
                <SkeletonText width="30%" />
              )}
            </span>
          </EntriesContainer>
          <div style={{ marginTop: '30px' }}>
            <EntriesContainer>
              <span style={{ fontWeight: 600 }}>Token Price</span>
              {!loading || (deadline.isPast === false && <span style={{ fontWeight: 600 }}>Cliff Deadline</span>)}
            </EntriesContainer>
            <EntriesContainer>
              <span>
                <SkeletonText width="50px" />
              </span>
              {!loading || (data && deadline.isPast === false && <span>{cliffDateFormatted}</span>)}
            </EntriesContainer>
          </div>

          <div style={{ marginTop: '30px' }}>
            <EntriesContainer>
              <span style={{ fontWeight: 600 }}>Token Address</span>
            </EntriesContainer>
            <EntriesContainer>
              {!loading && tokenAddress !== '' ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <span>{tokenAddress}</span>
                  <div style={{ maxWidth: '80px' }}>
                    <ButtonSecondary onClick={() => copyToClipboard(token?.address ? token.address : '')}>
                      {buttonLabel}
                    </ButtonSecondary>
                  </div>
                </div>
              ) : (
                <SkeletonText width="50px" />
              )}
            </EntriesContainer>
          </div>

          <div style={{ marginTop: '30px' }}>
            <EntriesContainer>
              <span style={{ fontWeight: 600, fontSize: '14px' }}>Claimed</span>
              <span style={{ fontWeight: 600, fontSize: '14px' }}>Outstanding</span>
            </EntriesContainer>
            <EntriesContainer>
              <span style={{ fontSize: '14px', fontWeight: 600, color: '#4afc38' }}>
                <span>
                  {data || !loading ? (
                    `${roundToDecimalPlace(claimedFormatted, '3')} $${tokenSymbol}`
                  ) : (
                    <SkeletonText width="30%" />
                  )}
                </span>
              </span>
              <span style={{ fontSize: '14px', fontWeight: 600, color: '#4afc38' }}>
                {data || !loading ? (
                  `${roundToDecimalPlace(outstandingFormatted, '3')} $${tokenSymbol}`
                ) : (
                  <SkeletonText width="30%" />
                )}
              </span>
            </EntriesContainer>
          </div>

          <div style={{ marginTop: '30px', display: 'flex', gap: '8px' }}>
            <div>
              <EntriesContainer>
                <span style={{ fontWeight: 600, fontSize: '14px' }}>Available to claim</span>
              </EntriesContainer>
              <EntriesContainer>
                <span style={{ fontSize: '14px', fontWeight: 600, color: '#4afc38' }}>
                  <span>
                    {data || !loading ? (
                      `${roundToDecimalPlace(availableFormatted, '3')} $${tokenSymbol}`
                    ) : (
                      <SkeletonText width="30%" />
                    )}
                  </span>
                </span>
              </EntriesContainer>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
              <div style={{ width: 'clamp(40px, 130px, 400px)', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <ButtonPrimary disabled={!isPast} onClick={() => onClaim()}>
                  {loadingClaim ? 'Claiming...' : 'Claim'}
                </ButtonPrimary>
                {!isPast && <span style={{ color: 'lightgrey' }}>The cliff has not been reached.</span>}
              </div>
            </div>
          </div>
        </>
      </SubContainer>
    </GridContainer>
  )
}
export default Vesting
